import { useTranslation } from "react-i18next";

import { EmptyState, ErrorState, Spinner } from "@/components/ui";
import { formatChatStamp } from "@/lib/format";
import { Avatar } from "./Avatar";
import type { Conversation, InboxFilter } from "./queries";
import { useConversations } from "./queries";
import { URGENCY_TONES, useWindowState } from "./window";

const FILTERS: readonly InboxFilter[] = ["mine", "unassigned", "all"];

function Row({
  conversation,
  isSelected,
  onSelect,
}: {
  conversation: Conversation;
  isSelected: boolean;
  onSelect: (id: number) => void;
}) {
  const { t } = useTranslation();
  const serviceWindow = useWindowState(conversation.window_expires_at);
  const unread = conversation.unread_count > 0;

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(conversation.id)}
        aria-current={isSelected}
        className={`flex w-full items-center gap-3 pl-3 text-left ${
          isSelected ? "bg-wa-panel" : "hover:bg-wa-hover"
        }`}
      >
        <Avatar />
        <div className="min-w-0 flex-1 border-b border-wa-line py-3 pr-3">
          <div className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate text-[17px] leading-snug">
              {conversation.profile_name || conversation.wa_id}
            </span>
            {/* Green while something is waiting, as WhatsApp does it. */}
            <span
              className={`shrink-0 text-xs ${unread ? "text-wa-badge" : "text-wa-meta"}`}
            >
              {formatChatStamp(conversation.last_message_at)}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-sm">
            <span
              className={`min-w-0 flex-1 truncate tabular-nums ${URGENCY_TONES[serviceWindow.urgency]}`}
            >
              {serviceWindow.isOpen
                ? `⏱ ${serviceWindow.label}`
                : t("inbox.closedShort")}
            </span>
            {unread ? (
              <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-wa-badge px-1.5 text-xs font-semibold text-white">
                {conversation.unread_count}
              </span>
            ) : null}
          </div>
          {conversation.company === null ? (
            // An unknown sender is a lead, not a client. Saying so in the list
            // is what makes the Recepción queue visible without a second screen.
            <span className="text-xs text-brand-700">{t("inbox.unknownSender")}</span>
          ) : null}
        </div>
      </button>
    </li>
  );
}

export function ConversationList({
  filter,
  onFilterChange,
  agentId,
  selectedId,
  onSelect,
}: {
  filter: InboxFilter;
  onFilterChange: (filter: InboxFilter) => void;
  agentId: number | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  const { t } = useTranslation();
  const query = useConversations(filter, agentId);
  const conversations = query.data?.results ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col border-wa-line bg-white sm:border-r">
      <h1 className="px-4 pt-4 pb-1 text-[22px] font-bold">{t("inbox.title")}</h1>
      <div className="flex gap-1.5 px-3 py-2">
        {FILTERS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onFilterChange(option)}
            aria-pressed={filter === option}
            className={`rounded-full px-2.5 py-1 text-sm whitespace-nowrap ${
              filter === option
                ? "bg-wa-out text-wa-green-dark"
                : "bg-wa-panel text-wa-icon hover:bg-wa-line"
            }`}
          >
            {t(`inbox.filter.${option}`)}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {query.isPending ? <Spinner label={t("inbox.loading")} /> : null}
        {query.isError ? (
          <ErrorState
            message={(query.error as Error).message}
            onRetry={() => void query.refetch()}
          />
        ) : null}
        {query.isSuccess && conversations.length === 0 ? (
          <EmptyState>{t("inbox.empty")}</EmptyState>
        ) : null}
        <ul>
          {conversations.map((conversation) => (
            <Row
              key={conversation.id}
              conversation={conversation}
              isSelected={conversation.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}
